import {
  buildFhirTranslationExtension,
  ITEM_FRAMING_URL,
  type PrimitiveTranslationElement,
  type QuestionnaireItemAnswerOptionWithTranslations,
  type QuestionnaireItemWithTranslations,
} from '@liza/fhir-utils';
import type { Extension, Observation } from '@medplum/fhirtypes';

/**
 * Shared machinery for the PHQ-style scored screening surveys (PHQ-9, GAD-7): instruments whose
 * items are required `choice` questions on a fixed 0–3 frequency scale and whose total score is
 * derived server-side by Medplum's SDC `QuestionnaireResponse/$extract`. The per-instrument files
 * ({@link ./phq9}, {@link ./gad7}) own the item wording, LOINC codes, canonical url, and the exact
 * (validated) translations; everything mechanical lives here so the two don't drift.
 */

/** The standard SDC `optionPrefix` extension — the leading "0"/"1"/… badge shown before an answer. */
export const OPTION_PREFIX_URL = 'http://hl7.org/fhir/StructureDefinition/questionnaire-optionPrefix';
export const ORDINAL_VALUE_URL = 'http://hl7.org/fhir/StructureDefinition/ordinalValue';
const TEMPLATE_EXTRACT_URL = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-templateExtract';
const TEMPLATE_EXTRACT_VALUE_URL =
  'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-templateExtractValue';

/** Builds an `es` FHIR translation extension sibling for a primitive element. */
export const withEs = (es: string): PrimitiveTranslationElement => ({
  extension: [buildFhirTranslationExtension('es', es)],
});

/**
 * One option on the standard 0–3 frequency scale: the LOINC answer coding plus the `optionPrefix`
 * (the leading "0"/"1"/… badge) and `ordinalValue` (the scored weight) extensions. The Spanish
 * `display` varies by instrument source, so it is passed in rather than fixed here.
 */
export const frequencyAnswerOption = (
  prefix: string,
  ordinal: number,
  code: string,
  display: string,
  displayEs: string,
): QuestionnaireItemAnswerOptionWithTranslations => ({
  extension: [
    { url: OPTION_PREFIX_URL, valueString: prefix },
    { url: ORDINAL_VALUE_URL, valueDecimal: ordinal },
  ],
  valueCoding: { system: 'http://loinc.org', code, display, _display: withEs(displayEs) },
});

/**
 * A bilingual framing prompt shown above a question (e.g. PHQ-9's "Over the last 2 weeks…").
 * Framing is optional per item and may differ between questions; an item with no framing stands
 * alone (PHQ-9 Q10).
 */
export type ItemFraming = { en: string; es: string };

/** Serializes an {@link ItemFraming} as the framing extension: the canonical URL + en/es siblings. */
const framingExtension = (framing: ItemFraming): Extension => ({
  url: ITEM_FRAMING_URL,
  extension: [
    { url: 'en', valueString: framing.en },
    { url: 'es', valueString: framing.es },
  ],
});

/**
 * A required (or optional) scored symptom item: a LOINC-coded `choice` over the frequency scale.
 * Pass `framing` to show a bilingual prompt above the question (PHQ-9 Q1–Q9); omit it for items
 * that stand alone (PHQ-9 Q10).
 */
export const symptomItem = (
  linkId: string,
  loincCode: string,
  loincDisplay: string,
  text: string,
  textEs: string,
  required: boolean,
  answerOptions: QuestionnaireItemAnswerOptionWithTranslations[],
  framing?: ItemFraming,
): QuestionnaireItemWithTranslations => ({
  linkId,
  code: [{ system: 'http://loinc.org', code: loincCode, display: loincDisplay }],
  text,
  _text: withEs(textEs),
  type: 'choice',
  required,
  answerOption: answerOptions,
  ...(framing ? { extension: [framingExtension(framing)] } : {}),
});

const templateExtractValue = (expression: string): { extension: Extension[] } => ({
  extension: [{ url: TEMPLATE_EXTRACT_VALUE_URL, valueString: expression }],
});

/**
 * Sums the `ordinalValue` extension carried on each chosen answer's coding. Medplum's FHIRPath
 * has no aggregate functions (`sum()`/`aggregate()` are unimplemented; `descendants()` and
 * `repeat()` are stubs returning empty), so the sum is a fixed-arity `+` chain over every item —
 * expressible only because all items are required. Empty operands propagate: if any answer is
 * missing its ordinal, the whole sum is empty and `$extract` drops the score (with no error), so
 * the submitting service must validate the extracted value.
 */
const totalScoreExpression = (scoredItems: QuestionnaireItemWithTranslations[]): string =>
  scoredItems
    .map((item) => `item.where(linkId = '${item.linkId}').answer.value.extension('${ORDINAL_VALUE_URL}').value`)
    .join(' + ');

/**
 * Builds the `$extract` target: a contained Observation carrying the *total-score* LOINC. subject,
 * effectiveDateTime, and derivedFrom are pulled from the triggering QuestionnaireResponse
 * (%resource); derivedFrom resolves only when the response is persisted before extraction, so its
 * id is populated. FHIR primitive extensions ride on underscore-prefixed sibling fields (e.g.
 * `_value`), which @medplum/fhirtypes does not model — hence the cast.
 */
export const buildScoreObservationTemplate = (params: {
  id: string;
  /** Result (total-score) code. A real LOINC, or a minted `survey-scores` code for instruments
   * without one — pair with `totalScoreSystem`. */
  totalScoreCode: string;
  /** Result code system. Defaults to LOINC; pass a `survey-scores` system for minted codes. */
  totalScoreSystem?: string;
  display: string;
  scoredItems: QuestionnaireItemWithTranslations[];
}): Observation =>
  ({
    resourceType: 'Observation',
    id: params.id,
    status: 'final',
    category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'survey' }] }],
    code: {
      coding: [
        { system: params.totalScoreSystem ?? 'http://loinc.org', code: params.totalScoreCode, display: params.display },
      ],
    },
    subject: { _reference: templateExtractValue('%resource.subject.reference') },
    _effectiveDateTime: templateExtractValue('%resource.authored'),
    derivedFrom: [{ _reference: templateExtractValue(`'QuestionnaireResponse/' + %resource.id`) }],
    valueQuantity: {
      unit: 'score',
      system: 'http://unitsofmeasure.org',
      code: '{score}',
      _value: templateExtractValue(totalScoreExpression(params.scoredItems)),
    },
  }) as unknown as Observation;

/** The `Questionnaire.extension` entry wiring SDC `$extract` to a contained Observation template. */
export const buildTemplateExtractExtension = (templateId: string): Extension => ({
  url: TEMPLATE_EXTRACT_URL,
  extension: [{ url: 'template', valueReference: { reference: `#${templateId}` } }],
});

/** The semver `artifact-versionAlgorithm` extension shared by these versioned canonical resources. */
export const SEMVER_VERSION_ALGORITHM_EXTENSION: Extension = {
  url: 'http://hl7.org/fhir/StructureDefinition/artifact-versionAlgorithm',
  valueCoding: { system: 'http://hl7.org/fhir/version-algorithm', code: 'semver' },
};
